/**
 * sharedFields — the ownership-driven shared-field model.
 *
 * A field whose `owner_form` differs from the form it is rendered on is a
 * CROSS-FORM SHARED field: it is owned (authored, validated) on the owner's
 * form and prefilled — one-shot, fill-blanks, editable, never re-synced — onto
 * this form. Example: the 40SP sponsor form carries 13 applicant-owned facts
 * (name, DOB, relationship dates) prefilled from the applicant's 820 intake.
 *
 * This is the single source of truth for "which fields does form X pull from
 * another form". Kept in its own module (no Supabase import) so it is unit
 * testable — the sharedFields snapshot guard freezes the exact sets so a schema
 * edit that over-shares (privacy risk) or under-shares (re-asking known data)
 * fails CI.
 */

import type { FormSchema } from '../schema-renderer/types';

/**
 * Collect all canonical_names declared in a schema that are NOT owned by this
 * form — i.e. cross-form shared fields prefilled from their owner form.
 *
 * Walks section.fields and section.subsections[].fields (the render surfaces
 * the prefill targets). Repeater entry_schema fields are intentionally not
 * recursed — prefill operates on top-level shared facts.
 */
export function collectSharedFieldNames(schema: FormSchema): string[] {
  const shared: string[] = [];
  for (const section of schema.sections) {
    const allFields = [
      ...(section.fields ?? []),
      ...(section.subsections ?? []).flatMap((s) => s.fields),
    ];
    for (const field of allFields) {
      if (field.owner_form && field.owner_form !== schema.form_id) {
        shared.push(field.canonical_name);
      }
    }
  }
  return shared;
}
